import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useModules } from '../hooks/useModules';
import { useAuth } from '../contexts/AuthContext';
import {
  Blocks, Search, RefreshCw, Lock, Crown, Shield, CheckCircle2,
  ChevronDown, ChevronUp, Zap, ShoppingCart, Lightbulb, ThumbsUp,
  Plus, ArrowUpRight, Package, Star, Pencil, BarChart3, Target
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  free:         { label: 'Free',         color: 'bg-gray-100 text-gray-700',     order: 0, price: 0 },
  starter:      { label: 'Starter',      color: 'bg-blue-100 text-blue-700',     order: 1, price: 29 },
  professional: { label: 'Professional', color: 'bg-violet-100 text-violet-700', order: 2, price: 79 },
  business:     { label: 'Business',     color: 'bg-amber-100 text-amber-700',   order: 3, price: 149 },
  enterprise:   { label: 'Enterprise',   color: 'bg-emerald-100 text-emerald-700', order: 4, price: 299 },
  // v2.5.868 — Add-on tier for paid modules like Vending Fleet.
  // Not part of the tier ladder; must be purchased explicitly or granted by Platform Admin.
  addon:        { label: 'Add-on',       color: 'bg-sky-100 text-sky-700',       order: 99, price: 0 },
};

const CATEGORY_LABELS = {
  overview: 'Overview', lead_generation: 'Lead Generation', sales: 'Sales',
  marketing: 'Marketing', retention: 'Customer Retention', service: 'Customer Service',
  operations: 'Operations', analytics: 'Analytics & Reports', website: 'Website & Compliance',
  settings: 'Platform Settings', tools: 'Tools',
};

const IDEA_STATUS = {
  submitted: { label: 'Submitted', color: 'bg-gray-100 text-gray-600' },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700' },
  evaluating: { label: 'Evaluating', color: 'bg-cyan-100 text-cyan-700' },
  planned: { label: 'Planned', color: 'bg-violet-100 text-violet-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  testing: { label: 'Testing', color: 'bg-orange-100 text-orange-700' },
  built: { label: 'Built', color: 'bg-emerald-100 text-emerald-700' },
  released: { label: 'Released', color: 'bg-green-100 text-green-700' },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700' },
  on_hold: { label: 'On Hold', color: 'bg-slate-100 text-slate-600' },
};

const GRADE_CONFIG = {
  'A+': { color: 'bg-emerald-500 text-white' },
  'A': { color: 'bg-emerald-100 text-emerald-700' },
  'B': { color: 'bg-blue-100 text-blue-700' },
  'C': { color: 'bg-amber-100 text-amber-700' },
  'D': { color: 'bg-orange-100 text-orange-700' },
  'F': { color: 'bg-red-100 text-red-700' },
};

export default function ModuleManagerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'my-modules';
  const [modules, setModules] = useState([]);
  const [userTier, setUserTier] = useState('free');
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');
  const { refresh: refreshSidebarModules } = useModules();

  const fetchModules = useCallback(async () => {
    setLoading(true);
    setApiError(false);
    setErrorDetail('');
    try {
      const res = await axios.get(`${API}/api/modules/registry`);
      setModules(res.data.modules || []);
      setUserTier(res.data.user_tier || 'free');
      setIsPlatformAdmin(!!res.data.is_platform_admin);
      refreshSidebarModules();
    } catch (e) {
      console.error('Module registry error:', e);
      setApiError(true);
      const detail = e.response?.data?.detail || e.message || 'Unknown error';
      setErrorDetail(detail);
      // Fallback: try to get user tier from auth/me
      try {
        const me = await axios.get(`${API}/api/auth/me`);
        if (me.data?.plan) setUserTier(me.data.plan);
      } catch { /* silent */ }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const tc = TIER_CONFIG[userTier] || TIER_CONFIG.free;
  const totalEnabled = modules.filter(m => m.is_enabled).length;
  const totalAccessible = modules.filter(m => m.has_access).length;

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="module-manager-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
            <Blocks className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Module Management</h1>
            <p className="text-gray-medium text-sm mt-0.5">Manage, shop, and request platform modules</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isPlatformAdmin ? (
            <Badge className="gap-1 text-xs bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0" data-testid="platform-owner-badge">
              <Crown className="h-3 w-3" />Platform Owner · Universal Access
            </Badge>
          ) : (
            <Badge className={cn("gap-1 text-xs", tc.color)} data-testid="current-tier-badge">
              <Crown className="h-3 w-3" />Your Plan: {tc.label}
            </Badge>
          )}
          <Badge variant="outline" className="text-xs gap-1">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />{totalEnabled} active
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchModules} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {apiError ? (
        <Card className="border-amber-200 bg-amber-50" data-testid="module-api-error">
          <CardContent className="p-5 text-center">
            <Blocks className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-amber-800 mb-1">Module registry is initializing</h3>
            <p className="text-sm text-amber-600 mb-1">This may happen on first load after a deployment. Please click retry.</p>
            {errorDetail && <p className="text-xs text-amber-500 mb-3 font-mono bg-amber-100 p-2 rounded break-all">{errorDetail}</p>}
            <Button onClick={fetchModules} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5">
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />Retry
            </Button>
            <p className="text-[10px] text-amber-400 mt-3">If retry doesn&apos;t work, try restarting the backend service on your server.</p>
          </CardContent>
        </Card>
      ) : (
      <Tabs defaultValue={initialTab} onValueChange={v => setSearchParams(v === 'my-modules' ? {} : { tab: v }, { replace: true })}>
        <TabsList className="overflow-x-auto flex-nowrap bg-gray-100/80">
          <TabsTrigger value="my-modules" className="gap-1.5 text-xs" data-testid="modules-tab-my"><Blocks className="h-3.5 w-3.5" />My Modules</TabsTrigger>
          <TabsTrigger value="shop" className="gap-1.5 text-xs" data-testid="modules-tab-shop"><ShoppingCart className="h-3.5 w-3.5" />Module Shop</TabsTrigger>
          <TabsTrigger value="ideas" className="gap-1.5 text-xs" data-testid="modules-tab-ideas"><Lightbulb className="h-3.5 w-3.5" />Request Ideas</TabsTrigger>
          {isPlatformAdmin && (
            <TabsTrigger value="tier-availability" className="gap-1.5 text-xs" data-testid="modules-tab-tier-availability">
              <Shield className="h-3.5 w-3.5" />Tier Availability
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-modules">
          <MyModulesView modules={modules} userTier={userTier} isPlatformAdmin={isPlatformAdmin} onToggle={fetchModules} />
        </TabsContent>
        <TabsContent value="shop">
          <ModuleShopView modules={modules} userTier={userTier} isPlatformAdmin={isPlatformAdmin} onPurchase={fetchModules} />
        </TabsContent>
        <TabsContent value="ideas">
          <ModuleIdeasView />
        </TabsContent>
        {isPlatformAdmin && (
          <TabsContent value="tier-availability">
            <TierAvailabilityView onSaved={fetchModules} />
          </TabsContent>
        )}
      </Tabs>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 1: My Modules — Activate / Deactivate
   ═══════════════════════════════════════════════════════════════════ */
function MyModulesView({ modules, userTier, onToggle }) {
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    const cats = new Set(modules.filter(m => m.has_access).map(m => m.category));
    setExpandedCats(cats);
  }, [modules]);

  const toggleModule = async (moduleId) => {
    setToggling(moduleId);
    try {
      const res = await axios.put(`${API}/api/modules/toggle/${moduleId}`);
      toast.success(`Module ${res.data.action}`);
      onToggle();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setToggling(null); }
  };

  const toggleCat = (cat) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  // Only show accessible modules
  let filtered = modules.filter(m => m.has_access);
  if (search) filtered = filtered.filter(m => m.name.toLowerCase().includes(search.toLowerCase()));
  if (filterCategory !== 'all') filtered = filtered.filter(m => m.category === filterCategory);

  const grouped = {};
  filtered.forEach(m => { if (!grouped[m.category]) grouped[m.category] = []; grouped[m.category].push(m); });

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search your modules..." className="pl-8" value={search} onChange={e => setSearch(e.target.value)} data-testid="my-modules-search" />
        </div>
        <select className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="all">All Categories</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {Object.entries(grouped).map(([category, mods]) => {
          const catLabel = CATEGORY_LABELS[category] || category;
          const isExpanded = expandedCats.has(category);
          const enabledCount = mods.filter(m => m.is_enabled).length;

          return (
            <Card key={category} className="bg-white border-gray-border overflow-hidden" data-testid={`my-module-group-${category}`}>
              <div className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-gray-50/50 transition-colors" onClick={() => toggleCat(category)}>
                <div className="flex items-center gap-2">
                  <Blocks className="h-3.5 w-3.5 text-gray-400" />
                  <h3 className="text-sm font-semibold text-text-primary">{catLabel}</h3>
                  <Badge variant="outline" className="text-[10px]">{enabledCount}/{mods.length}</Badge>
                </div>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </div>
              {isExpanded && (
                <div className="border-t divide-y divide-gray-50">
                  {mods.map(mod => (
                    <div key={mod.module_id} className="flex items-center gap-3 px-4 py-2 transition-all" data-testid={`module-row-${mod.module_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-primary">{mod.name}</p>
                          {mod.is_core && <Badge className="text-[9px] bg-slate-100 text-slate-500 gap-0.5"><Shield className="h-2.5 w-2.5" />Core</Badge>}
                          {mod.is_addon && <Badge className="text-[9px] bg-violet-100 text-violet-600 gap-0.5"><Package className="h-2.5 w-2.5" />Add-on</Badge>}
                          {mod.coming_soon && <Badge className="text-[9px] bg-amber-100 text-amber-600 gap-0.5">Coming Soon</Badge>}
                        </div>
                        {mod.description && <p className="text-[11px] text-gray-400 mt-0.5">{mod.description}</p>}
                      </div>
                      <Badge className={cn("text-[10px] flex-shrink-0", (TIER_CONFIG[mod.tier] || TIER_CONFIG.free).color)}>{(TIER_CONFIG[mod.tier] || TIER_CONFIG.free).label}</Badge>
                      {mod.coming_soon ? (
                        <Badge className="text-[10px] bg-amber-50 text-amber-600 gap-1">Coming Soon</Badge>
                      ) : mod.is_core ? (
                        <Badge className="text-[10px] bg-emerald-50 text-emerald-600 gap-1"><CheckCircle2 className="h-3 w-3" />Always On</Badge>
                      ) : (
                        <Switch checked={mod.is_enabled} onCheckedChange={() => toggleModule(mod.module_id)} disabled={toggling === mod.module_id} data-testid={`toggle-${mod.module_id}`} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
      {filtered.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No modules match your search.</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 2: Module Shop — Browse & Purchase Add-ons
   ═══════════════════════════════════════════════════════════════════ */
function ModuleShopView({ modules, userTier, onPurchase }) {
  const { user } = useAuth();
  const isAtTopTier = user?.role === 'super_admin' || (user?.plan || '').toLowerCase() === 'enterprise';
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [purchasing, setPurchasing] = useState(null);
  const [upgradeModule, setUpgradeModule] = useState(null); // module selected for upgrade dialog

  const userTierCfg = TIER_CONFIG[userTier] || TIER_CONFIG.free;
  const userTierOrder = userTierCfg.order;

  let shopModules = [...modules];
  if (search) shopModules = shopModules.filter(m => m.name.toLowerCase().includes(search.toLowerCase()) || (m.description || '').toLowerCase().includes(search.toLowerCase()));
  if (filterTier !== 'all') shopModules = shopModules.filter(m => m.tier === filterTier);

  shopModules.sort((a, b) => {
    if (a.has_access === b.has_access) return (a.name > b.name ? 1 : -1);
    return a.has_access ? 1 : -1;
  });

  const lockedCount = shopModules.filter(m => !m.has_access).length;
  // v2.5.868 — addonCount now counts modules the tenant has EXPLICITLY purchased
  // (or been comped by Platform Admin), not every add-on-eligible module.
  const addonCount = modules.filter(m => m.is_purchased_addon).length;

  const handlePurchaseAddon = async (moduleId) => {
    setPurchasing(moduleId);
    try {
      await axios.post(`${API}/api/modules/addon/${moduleId}`);
      toast.success('Module add-on activated!');
      setUpgradeModule(null);
      onPurchase();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to activate add-on'); }
    finally { setPurchasing(null); }
  };

  // Calculate what you get if you upgrade to the module's tier
  const getUpgradeInfo = (mod) => {
    const modTierCfg = TIER_CONFIG[mod.tier] || TIER_CONFIG.free;
    const modulesInTier = modules.filter(m => {
      const mTier = TIER_CONFIG[m.tier] || TIER_CONFIG.free;
      return mTier.order <= modTierCfg.order && !m.has_access;
    });
    return { tierCfg: modTierCfg, unlockCount: modulesInTier.length, tierPrice: modTierCfg.price };
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Shop Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border-gray-border"><CardContent className="p-3">
          <p className="text-xs text-gray-400">Total Available</p>
          <p className="text-xl font-bold">{modules.length}</p>
        </CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3">
          <p className="text-xs text-gray-400">In Your Plan</p>
          <p className="text-xl font-bold text-emerald-600">{modules.filter(m => m.has_access && !m.is_addon).length}</p>
        </CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3">
          <p className="text-xs text-gray-400">Add-ons Active</p>
          <p className="text-xl font-bold text-violet-600">{addonCount}</p>
        </CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3">
          <p className="text-xs text-gray-400">Available to Add</p>
          <p className="text-xl font-bold text-blue-600">{lockedCount}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search all modules..." className="pl-8" value={search} onChange={e => setSearch(e.target.value)} data-testid="module-shop-search" />
        </div>
        <select className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" value={filterTier} onChange={e => setFilterTier(e.target.value)} data-testid="module-shop-tier-filter">
          <option value="all">All Tiers</option>
          {Object.entries(TIER_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Module Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {shopModules.map(mod => {
          const tierCfg = TIER_CONFIG[mod.tier] || TIER_CONFIG.free;
          const isAddon = mod.is_addon;

          return (
            <Card key={mod.module_id} className={cn("bg-white border overflow-hidden transition-all hover:shadow-sm", !mod.has_access && "border-dashed border-gray-300")} data-testid={`shop-module-${mod.module_id}`}>
              <CardContent className="p-3.5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{mod.name}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{CATEGORY_LABELS[mod.category] || mod.category}</p>
                  </div>
                  <Badge className={cn("text-[10px] flex-shrink-0", tierCfg.color)}>{tierCfg.label}</Badge>
                </div>
                {mod.description && <p className="text-[11px] text-gray-500 mb-3 line-clamp-2">{mod.description}</p>}

                {mod.coming_soon ? (
                  <Badge className="text-[10px] bg-amber-50 text-amber-600 gap-1 w-full justify-center py-1">Coming Soon</Badge>
                ) : mod.is_purchased_addon ? (
                  <Badge className="text-[10px] bg-violet-50 text-violet-600 gap-1 w-full justify-center py-1"><Package className="h-3 w-3" />Add-on Active</Badge>
                ) : isAddon && mod.has_access ? (
                  // Module is defined as an add-on and user has bypass access (Platform Admin)
                  <Badge className="text-[10px] bg-sky-50 text-sky-700 gap-1 w-full justify-center py-1"><Package className="h-3 w-3" />Included (Platform Admin)</Badge>
                ) : mod.has_access ? (
                  <Badge className="text-[10px] bg-emerald-50 text-emerald-600 gap-1 w-full justify-center py-1"><CheckCircle2 className="h-3 w-3" />Included in Plan</Badge>
                ) : isAddon && mod.addon_price ? (
                  <Button
                    size="sm"
                    className="w-full h-7 text-xs gap-1.5 bg-sky-600 hover:bg-sky-700 text-white"
                    onClick={() => setUpgradeModule(mod)}
                    data-testid={`buy-addon-${mod.module_id}`}
                  >
                    <ShoppingCart className="h-3 w-3" />Buy Add-on — ${mod.addon_price}/mo
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="w-full h-7 text-xs gap-1.5 bg-slate-800 hover:bg-slate-700 text-white"
                    onClick={() => setUpgradeModule(mod)}
                    data-testid={`buy-addon-${mod.module_id}`}
                  >
                    <ShoppingCart className="h-3 w-3" />Add to My Plan
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {shopModules.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No modules match your search.</p>}

      {/* Upgrade Banner — hidden for super_admin (owns the platform) and
          enterprise subscribers (already on the top tier; nowhere to upgrade). */}
      {!isAtTopTier && (
        <Card className="bg-gradient-to-r from-slate-800 to-emerald-900 text-white" data-testid="upgrade-banner">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm">Want more modules included by default?</p>
              <p className="text-xs text-slate-300 mt-0.5">Upgrade your plan and unlock entire tiers of features.</p>
            </div>
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1">
              <ArrowUpRight className="h-3.5 w-3.5" />View Plans
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Upgrade Decision Dialog ── */}
      <Dialog open={!!upgradeModule} onOpenChange={(open) => { if (!open) setUpgradeModule(null); }}>
        <DialogContent className="max-w-md" data-testid="upgrade-decision-dialog">
          {upgradeModule && (() => {
            const info = getUpgradeInfo(upgradeModule);
            const modTierCfg = info.tierCfg;
            // v2.5.895 — Use the REAL add-on price from the module registry
            // (upgradeModule.addon_price + optional addon_one_time_price) instead
            // of the fake "15% of tier" calculation that showed $5/mo for the
            // $299/mo Vending Fleet module. If the module is a paid add-on, we
            // MUST route through Stripe checkout — no more instant-add loopholes.
            const isPaidAddon = !!upgradeModule.is_addon && !!upgradeModule.addon_price;
            // v2.5.904 — Only real paid add-ons expose a price. Non-add-on
            // modules go through the tier-upgrade path and do NOT get a
            // synthetic $5 or $0 "add-on" price. This kills the misleading
            // "USD 0 add-on" that used to appear for non-paid modules.
            const addonPrice = upgradeModule.addon_price || null;
            const oneTimePrice = upgradeModule.addon_one_time_price || null;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-lg">
                    <ShoppingCart className="h-5 w-5 text-emerald-600" />
                    Add &quot;{upgradeModule.name}&quot;
                  </DialogTitle>
                </DialogHeader>

                <p className="text-sm text-gray-500 -mt-1">
                  {isPaidAddon
                    ? <>This is a paid module. Choose how you want to access it — subscribe monthly, or buy the module outright (one-time payment).</>
                    : <>This module requires the <Badge className={cn("text-[10px] mx-1", modTierCfg.color)}>{modTierCfg.label}</Badge> tier. Choose how to get it:</>
                  }
                </p>

                <div className="space-y-3 mt-2">
                  {/* Option A: Subscribe monthly */}
                  <Card
                    className="border-2 border-slate-200 hover:border-emerald-400 transition-colors cursor-pointer group"
                    onClick={() => {
                      if (isPaidAddon) {
                        window.location.href = `/checkout?module=${upgradeModule.module_id}&cycle=monthly&price=${addonPrice}`;
                      } else {
                        handlePurchaseAddon(upgradeModule.module_id);
                      }
                    }}
                    data-testid="upgrade-option-addon"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-200 transition-colors">
                          <Package className="h-5 w-5 text-violet-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-slate-800">
                              {isPaidAddon ? 'Subscribe Monthly' : 'Add as Individual Module'}
                            </h3>
                            <p className="text-sm font-bold text-violet-600">
                              ${addonPrice}/mo
                            </p>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            {isPaidAddon
                              ? 'Rolling subscription. Cancel any time. Secure Stripe checkout.'
                              : `Activate just this module on top of your current ${userTierCfg.label} plan.`
                            }
                          </p>
                          {purchasing === upgradeModule.module_id && <p className="text-[11px] text-emerald-600 mt-1 font-medium">Activating...</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Option A2: Buy outright (only for paid add-ons with a one-time price) */}
                  {isPaidAddon && oneTimePrice && (
                    <Card
                      className="border-2 border-slate-200 hover:border-amber-400 transition-colors cursor-pointer group"
                      onClick={() => { window.location.href = `/checkout?module=${upgradeModule.module_id}&cycle=one_time&price=${oneTimePrice}`; }}
                      data-testid="upgrade-option-onetime"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
                            <Crown className="h-5 w-5 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="text-sm font-semibold text-slate-800">Buy Module Outright</h3>
                              <p className="text-sm font-bold text-amber-600">${oneTimePrice.toLocaleString()} one-time</p>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              Own the module for life — pay once, no recurring fees. Best value for long-term operators.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Option B: Upgrade Tier */}
                  <Card className="border-2 border-slate-200 hover:border-emerald-400 transition-colors cursor-pointer group bg-gradient-to-r from-slate-50 to-emerald-50/30" data-testid="upgrade-option-tier">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                          <Crown className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-slate-800">Upgrade to {modTierCfg.label}</h3>
                            <p className="text-sm font-bold text-emerald-600">${modTierCfg.price}/mo</p>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            Unlock <span className="font-semibold text-emerald-700">{info.unlockCount} modules</span> included in the {modTierCfg.label} plan.
                          </p>
                          <div className="flex items-center gap-1.5 mt-2">
                            <Badge variant="outline" className="text-[9px] text-emerald-600 border-emerald-200">Best Value</Badge>
                            {userTierCfg.price > 0 && (
                              <span className="text-[10px] text-gray-400">Currently ${userTierCfg.price}/mo → ${modTierCfg.price}/mo</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="flex justify-end mt-2">
                  <Button variant="ghost" size="sm" className="text-xs text-gray-400" onClick={() => setUpgradeModule(null)}>
                    Cancel
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 3: Request Ideas — Submit & Vote on Module Ideas
   ═══════════════════════════════════════════════════════════════════ */
function ModuleIdeasView() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'general' });
  const [submitting, setSubmitting] = useState(false);
  const [voting, setVoting] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhanced, setEnhanced] = useState(null);
  const [managingIdea, setManagingIdea] = useState(null);
  const [adminForm, setAdminForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchIdeas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/modules/ideas`);
      setIdeas(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchIdeas(); }, [fetchIdeas]);

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      // Include enhanced spec in description if available
      const payload = { ...form };
      if (enhanced) {
        const spec = [
          form.description,
          '',
          '--- AI-Enhanced Specification ---',
          `Key Features: ${(enhanced.key_features || []).join(', ')}`,
          `User Stories: ${(enhanced.user_stories || []).join(' | ')}`,
          `Suggested Settings: ${(enhanced.suggested_settings || []).join(', ')}`,
          `Integrations: ${(enhanced.integrations || []).join(', ')}`,
          `KPIs: ${(enhanced.kpi_metrics || []).join(', ')}`,
          `Industries: ${(enhanced.industry_relevance || []).join(', ')}`,
        ].join('\n');
        payload.description = spec;
      }
      const res = await axios.post(`${API}/api/modules/ideas`, payload);
      setIdeas(prev => [res.data, ...prev]);
      toast.success('Idea submitted!');
      setShowNew(false);
      setForm({ title: '', description: '', category: 'general' });
      setEnhanced(null);
    } catch { toast.error('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handlePowerUp = async () => {
    if (!form.title.trim()) { toast.error('Enter a title first'); return; }
    setEnhancing(true);
    setEnhanced(null);
    try {
      const res = await axios.post(`${API}/api/modules/ideas/powerup`, form);
      if (res.data.ok && res.data.enhanced) {
        const e = res.data.enhanced;
        setEnhanced(e);
        // Only update title and category from AI, keep description as clean text
        setForm(f => ({
          ...f,
          title: e.title || f.title,
          description: e.description || f.description,
          category: e.category && CATEGORY_LABELS[e.category] ? e.category : f.category,
        }));
        toast.success('Idea enhanced by AI!');
      } else {
        toast.error(res.data.error || 'Enhancement failed');
      }
    } catch { toast.error('AI enhancement failed'); }
    finally { setEnhancing(false); }
  };

  const handleVote = async (ideaId) => {
    setVoting(ideaId);
    try {
      const res = await axios.post(`${API}/api/modules/ideas/${ideaId}/vote`);
      toast.success(res.data.action === 'voted' ? 'Upvoted!' : 'Vote removed');
      fetchIdeas();
    } catch { toast.error('Failed to vote'); }
    finally { setVoting(null); }
  };

  const openManage = (idea) => {
    setManagingIdea(idea);
    setAdminForm({
      status: idea.status || 'submitted',
      progress_pct: idea.progress_pct || 0,
      potential_grade: idea.potential_grade || '',
      priority: idea.priority || 'medium',
      assigned_to: idea.assigned_to || '',
      estimated_effort: idea.estimated_effort || '',
      target_release: idea.target_release || '',
      admin_notes: idea.admin_notes || '',
    });
  };

  const handleAdminSave = async () => {
    if (!managingIdea) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/modules/ideas/${managingIdea.id}`, adminForm);
      toast.success('Idea updated');
      setManagingIdea(null);
      fetchIdeas();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const filteredIdeas = filterStatus === 'all' ? ideas : ideas.filter(i => i.status === filterStatus);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-500">{ideas.length} idea{ideas.length !== 1 ? 's' : ''}</p>
          <select className="px-2 py-1 rounded-lg border text-xs bg-white" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="ideas-filter-status">
            <option value="all">All Statuses</option>
            {Object.entries(IDEA_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <Button size="sm" className="gap-1.5 bg-slate-800 hover:bg-slate-700 text-white" onClick={() => setShowNew(true)} data-testid="submit-idea-btn">
          <Plus className="h-3.5 w-3.5" />Submit Idea
        </Button>
      </div>

      {/* Ideas List */}
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : filteredIdeas.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <Lightbulb className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-700 mb-1">{filterStatus === 'all' ? 'No ideas yet' : `No ${IDEA_STATUS[filterStatus]?.label || filterStatus} ideas`}</h3>
            <p className="text-sm text-gray-500 mb-4">Be the first to suggest a new module!</p>
            <Button size="sm" className="bg-slate-800 hover:bg-slate-700 text-white gap-1.5" onClick={() => setShowNew(true)}>
              <Plus className="h-3.5 w-3.5" />Submit Idea
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredIdeas.map(idea => {
            const statusCfg = IDEA_STATUS[idea.status] || IDEA_STATUS.submitted;
            const gradeCfg = GRADE_CONFIG[idea.potential_grade];
            const progress = idea.progress_pct || 0;
            return (
              <Card key={idea.id} className="bg-white border-gray-border" data-testid={`idea-card-${idea.id}`}>
                <CardContent className="p-3.5 flex gap-3">
                  {/* Vote */}
                  <button onClick={() => handleVote(idea.id)} disabled={voting === idea.id} className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors flex-shrink-0" data-testid={`vote-idea-${idea.id}`}>
                    <ThumbsUp className={cn("h-4 w-4", idea.votes > 0 ? "text-emerald-600" : "text-gray-400")} />
                    <span className="text-xs font-bold text-slate-700">{idea.votes || 0}</span>
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-800">{idea.title}</h3>
                      <Badge className={cn("text-[10px]", statusCfg.color)}>{statusCfg.label}</Badge>
                      {gradeCfg && <Badge className={cn("text-[10px] font-bold", gradeCfg.color)}>Grade: {idea.potential_grade}</Badge>}
                      {idea.priority && idea.priority !== 'medium' && (
                        <Badge variant="outline" className={cn("text-[10px]", idea.priority === 'critical' ? 'text-red-600 border-red-300' : idea.priority === 'high' ? 'text-amber-600 border-amber-300' : 'text-gray-500')}>{idea.priority}</Badge>
                      )}
                    </div>
                    {idea.description && <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{idea.description}</p>}

                    {/* Progress bar for in-progress ideas */}
                    {progress > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-[10px] font-medium text-gray-500">{progress}%</span>
                      </div>
                    )}

                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <p className="text-[10px] text-gray-400">by {idea.submitted_by || 'Anonymous'} · {idea.category}</p>
                      {idea.assigned_to && <p className="text-[10px] text-blue-500">Assigned: {idea.assigned_to}</p>}
                      {idea.target_release && <Badge variant="outline" className="text-[9px]">{idea.target_release}</Badge>}
                      {idea.estimated_effort && <Badge variant="outline" className="text-[9px] capitalize">{idea.estimated_effort}</Badge>}
                    </div>
                  </div>

                  {/* Admin Manage Button */}
                  <Button variant="ghost" size="sm" className="flex-shrink-0 h-8 w-8 p-0 text-gray-400 hover:text-slate-700" onClick={() => openManage(idea)} data-testid={`manage-idea-${idea.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Admin Manage Idea Dialog */}
      <Dialog open={!!managingIdea} onOpenChange={(open) => { if (!open) setManagingIdea(null); }}>
        <DialogContent className="max-w-md" data-testid="manage-idea-dialog">
          {managingIdea && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base"><Target className="h-4 w-4 text-blue-500" />Manage: {managingIdea.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {/* Status + Progress Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
                    <select className="w-full px-3 py-2 rounded-lg border text-sm bg-white" value={adminForm.status} onChange={e => setAdminForm(p => ({ ...p, status: e.target.value }))} data-testid="manage-status">
                      {Object.entries(IDEA_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Progress: {adminForm.progress_pct}%</p>
                    <input type="range" min="0" max="100" step="5" value={adminForm.progress_pct} onChange={e => setAdminForm(p => ({ ...p, progress_pct: parseInt(e.target.value) }))} className="w-full accent-emerald-500" data-testid="manage-progress" />
                  </div>
                </div>

                {/* Grade + Priority */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Potential Grade</p>
                    <select className="w-full px-3 py-2 rounded-lg border text-sm bg-white" value={adminForm.potential_grade} onChange={e => setAdminForm(p => ({ ...p, potential_grade: e.target.value }))} data-testid="manage-grade">
                      <option value="">Not Graded</option>
                      {Object.keys(GRADE_CONFIG).map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Priority</p>
                    <select className="w-full px-3 py-2 rounded-lg border text-sm bg-white" value={adminForm.priority} onChange={e => setAdminForm(p => ({ ...p, priority: e.target.value }))} data-testid="manage-priority">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                {/* Effort + Target Release */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Estimated Effort</p>
                    <select className="w-full px-3 py-2 rounded-lg border text-sm bg-white" value={adminForm.estimated_effort} onChange={e => setAdminForm(p => ({ ...p, estimated_effort: e.target.value }))}>
                      <option value="">Not Estimated</option>
                      <option value="small">Small (1-2 days)</option>
                      <option value="medium">Medium (3-5 days)</option>
                      <option value="large">Large (1-2 weeks)</option>
                      <option value="epic">Epic (3+ weeks)</option>
                    </select>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Target Release</p>
                    <Input value={adminForm.target_release} onChange={e => setAdminForm(p => ({ ...p, target_release: e.target.value }))} placeholder="e.g. Q2 2026, v3.5" />
                  </div>
                </div>

                {/* Assigned To */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Assigned To</p>
                  <Input value={adminForm.assigned_to} onChange={e => setAdminForm(p => ({ ...p, assigned_to: e.target.value }))} placeholder="Team member or department" />
                </div>

                {/* Admin Notes */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Admin Notes</p>
                  <Textarea value={adminForm.admin_notes} onChange={e => setAdminForm(p => ({ ...p, admin_notes: e.target.value }))} rows={2} placeholder="Internal notes, evaluation comments..." />
                </div>

                {/* Timeline Preview */}
                {managingIdea.timeline?.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-2.5 border max-h-32 overflow-y-auto">
                    <p className="text-[10px] font-semibold text-gray-400 mb-1.5">Activity Timeline</p>
                    {managingIdea.timeline.slice(-5).reverse().map((t, i) => (
                      <p key={i} className="text-[10px] text-gray-500">{t.action}{t.to ? `: ${t.to}` : ''} — {t.by} · {t.at?.slice(0, 10)}</p>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter className="mt-2">
                <Button variant="outline" size="sm" onClick={() => setManagingIdea(null)}>Cancel</Button>
                <Button size="sm" onClick={handleAdminSave} disabled={saving} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="manage-save-btn">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Submit Idea Dialog */}
      <Dialog open={showNew} onOpenChange={(open) => { setShowNew(open); if (!open) { setEnhanced(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="submit-idea-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lightbulb className="h-5 w-5 text-amber-500" />Submit Module Idea</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Title</p>
              <Input placeholder="e.g., Inventory Management Module" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="idea-title-input" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
              <Textarea placeholder="Describe what this module should do..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} data-testid="idea-description-input" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Category</p>
              <select className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} data-testid="idea-category-select">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                <option value="general">General</option>
              </select>
            </div>

            {/* AI Power-Up Button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePowerUp}
              disabled={enhancing || !form.title.trim()}
              className="w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 transition-all"
              data-testid="idea-powerup-btn"
            >
              {enhancing ? (
                <><Zap className="h-3.5 w-3.5 animate-spin" />AI is thinking...</>
              ) : (
                <><Zap className="h-3.5 w-3.5" />Prompt Power-Up — Enhance with AI</>
              )}
            </Button>

            {/* Enhanced Specification Output */}
            {enhanced && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3 space-y-2.5 animate-fadeIn" data-testid="idea-enhanced-output">
                <div className="flex items-center gap-1.5 text-amber-700">
                  <Zap className="h-3.5 w-3.5" />
                  <p className="text-xs font-semibold">AI-Enhanced Specification</p>
                </div>

                {enhanced.key_features?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Key Features</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {enhanced.key_features.map((f, i) => <Badge key={i} variant="outline" className="text-[10px] bg-white">{f}</Badge>)}
                    </div>
                  </div>
                )}

                {enhanced.user_stories?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">User Stories</p>
                    <ul className="mt-1 space-y-0.5">
                      {enhanced.user_stories.slice(0, 3).map((s, i) => <li key={i} className="text-[11px] text-gray-600">{s}</li>)}
                    </ul>
                  </div>
                )}

                {enhanced.integrations?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Integrates With</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {enhanced.integrations.map((m, i) => <Badge key={i} className="text-[10px] bg-emerald-50 text-emerald-600 border-emerald-200">{m}</Badge>)}
                    </div>
                  </div>
                )}

                {enhanced.kpi_metrics?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">KPI Metrics</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {enhanced.kpi_metrics.map((k, i) => <Badge key={i} variant="outline" className="text-[10px] bg-white text-blue-600 border-blue-200">{k}</Badge>)}
                    </div>
                  </div>
                )}

                {enhanced.industry_relevance?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Best For Industries</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{enhanced.industry_relevance.join(' · ')}</p>
                  </div>
                )}

                {enhanced.suggested_settings?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Suggested Settings</p>
                    <ul className="mt-1 space-y-0.5">
                      {enhanced.suggested_settings.slice(0, 4).map((s, i) => <li key={i} className="text-[10px] text-gray-500">{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => { setShowNew(false); setEnhanced(null); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !form.title.trim()} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="idea-submit-btn">
              {submitting ? 'Submitting...' : enhanced ? 'Submit Enhanced Idea' : 'Submit Idea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// v2.5.876 — Platform Owner tool: toggle module availability per tier globally.
// A module can be `disabled_tiers` (visible but paywalled) or `hidden_tiers`
// (removed from sidebar + Module Manager entirely) for any tier while WIP.
function TierAvailabilityView({ onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [q, setQ] = useState('');

  const TIERS = ['starter', 'professional', 'business', 'enterprise', 'agency'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/modules/admin/tier-availability`);
      setRows(data.modules || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load tier availability');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (mod, disabled_tiers, hidden_tiers) => {
    setSaving(mod.module_id);
    try {
      await axios.put(`${API}/api/modules/admin/tier-availability`, {
        module_id: mod.module_id, disabled_tiers, hidden_tiers,
      });
      setRows(prev => prev.map(r => r.module_id === mod.module_id ? { ...r, disabled_tiers, hidden_tiers } : r));
      toast.success(`Updated ${mod.name}`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(null); }
  };

  const toggleTier = (mod, mode, tier) => {
    const key = mode === 'disable' ? 'disabled_tiers' : 'hidden_tiers';
    const cur = new Set(mod[key] || []);
    if (cur.has(tier)) cur.delete(tier); else cur.add(tier);
    const disabled = mode === 'disable' ? Array.from(cur) : (mod.disabled_tiers || []);
    const hidden = mode === 'hide' ? Array.from(cur) : (mod.hidden_tiers || []);
    save(mod, disabled, hidden);
  };

  const filtered = rows.filter(r => !q || r.name?.toLowerCase().includes(q.toLowerCase()) || r.module_id.includes(q.toLowerCase()));

  if (loading) return <div className="p-6 text-sm text-gray-medium flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4" data-testid="tier-availability-view">
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-xs sm:text-sm text-amber-900">
              <b>Platform Owner control.</b> Check <b>Disable</b> to keep a module visible on a tier but block access (paywall). Check <b>Hide</b> to remove the module entirely from that tier&apos;s sidebar + shop (use for WIP modules like Social Ads). Super_admin is never affected.
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-medium" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter modules…" className="pl-9 h-9 text-sm" data-testid="tier-avail-search" />
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-xs sm:text-sm min-w-[720px]">
          <thead className="bg-gray-50 border-b text-gray-medium">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Module</th>
              {TIERS.map(t => <th key={t} className="text-center px-2 py-2 font-medium capitalize">{t}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map(mod => (
              <tr key={mod.module_id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`tier-row-${mod.module_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium truncate max-w-[200px]">{mod.name}</div>
                  <div className="text-[10px] text-gray-medium truncate max-w-[200px]">{mod.category} · {mod.tier}{mod.is_addon ? ' · add-on' : ''}</div>
                </td>
                {TIERS.map(t => {
                  const isDisabled = (mod.disabled_tiers || []).includes(t);
                  const isHidden = (mod.hidden_tiers || []).includes(t);
                  const isBusy = saving === mod.module_id;
                  return (
                    <td key={t} className="px-2 py-2 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <label className="flex items-center gap-1 text-[10px] text-orange-700 cursor-pointer">
                          <input type="checkbox" checked={isDisabled} disabled={isBusy} onChange={() => toggleTier(mod, 'disable', t)} data-testid={`disable-${mod.module_id}-${t}`} />
                          Disable
                        </label>
                        <label className="flex items-center gap-1 text-[10px] text-red-700 cursor-pointer">
                          <input type="checkbox" checked={isHidden} disabled={isBusy} onChange={() => toggleTier(mod, 'hide', t)} data-testid={`hide-${mod.module_id}-${t}`} />
                          Hide
                        </label>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={TIERS.length + 1} className="p-4 text-center text-gray-medium">No modules match your filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
